/* v11.8-saree-first
 ============================================================
 * Tarunima Add-ons Controller — v11.7 (fast orphan cleanup)
 * ------------------------------------------------------------
 * CHANGE vs v11.6 (intentionally narrow — orphan cleanup only):
 *   - Removing a saree used to take ~3-5s before the matching
 *     F&P disappeared. Two round-trips were involved (one per
 *     orphan removed via /cart/change.js, then another to
 *     re-render the drawer via /cart/update.js) plus a 350ms
 *     debounce that reset every time the theme fired another
 *     cart event.
 *   - v11.7 collapses cleanup into a SINGLE /cart/update.js
 *     POST that does (a) zero-quantity for every orphan F&P
 *     line and (b) returns the rendered drawer sections in
 *     the same response.
 *   - Debounce dropped from 350ms to 50ms (just enough to
 *     coalesce simultaneous events).
 *   - NOTHING else changed: Add-to-Bag flow, F&P qty matching
 *     saree qty, button spinner, sections, error handling,
 *     debug helpers, fetch/XHR/event interception — all
 *     byte-identical to v11.6.
 *
 * CHANGE vs v11.5:
 *   - F&P quantity in cart now matches saree quantity, instead
 *     of being a fixed 1. Buy 2 sarees → 2 F&P services. Buy 3
 *     sarees → 3 F&P services. Etc.
 *   - On-PDP "Your total" preview now multiplies F&P price by
 *     saree qty too, so the previewed total matches the cart.
 *
 * FIXES vs v11.4:
 *   - qty=1 bug when F&P NOT selected: the theme's native
 *     submit reads FormData(form), which silently misses our
 *     qty input (it lives outside <form> and uses HTML5
 *     form="..." association). Result: qty=2 became qty=1
 *     whenever the customer didn't tick F&P.
 *   - no spinner when F&P NOT selected: same reason — we used
 *     to defer to the theme on that path, and the theme has no
 *     loading state we're guaranteed to see.
 *
 * Now: every Add-to-Bag click goes through our single-POST
 * path. Items array contains just the saree (if no F&P) or
 * saree + F&P (if ticked). Spinner shown in both cases.
 *
 * Debug: window.tnDebug() | window.tnCleanup()
 * ============================================================ */
(function () {
  'use strict';

  var VERSION = 'v11.7-fast-orphan-cleanup';
  var FP_VARIANT_ID = 52214935290139;

  try { console.log('%c[tn-addons] loaded ' + VERSION,
    'color:#8b6f3f;font-weight:bold'); } catch (e) {}

  // ---- Cart helpers ----------------------------------------------
  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.text().then(function (t) {
          var msg;
          try { msg = JSON.parse(t).description || JSON.parse(t).message; }
          catch (e) { msg = t; }
          throw new Error(msg || ('HTTP ' + r.status));
        });
      }
      return r.json();
    });
  }

  // ---- Section render helpers ------------------------------------
  // Collect ALL renderable sections from any cart element on the
  // page, then trim to ONLY the ones we need to repaint after an
  // Add-to-Bag. Each extra section costs a server-side render, so
  // we keep this minimal: the drawer + the header icon bubble.
  var KEEP_SECTIONS = /cart-drawer|cart-icon-bubble/i;
  function collectSections() {
    var all = [];
    var seen = {};
    var elements = [
      document.querySelector('cart-drawer'),
      document.querySelector('cart-notification'),
      document.querySelector('cart-items'),
      document.querySelector('cart-drawer-items')
    ];
    elements.forEach(function (el) {
      if (!el || typeof el.getSectionsToRender !== 'function') return;
      try {
        el.getSectionsToRender().forEach(function (s) {
          if (!s || !s.section || seen[s.section]) return;
          if (!KEEP_SECTIONS.test(s.section)) return; // trim heavy sections
          seen[s.section] = true;
          all.push({
            id: s.id,
            section: s.section,
            selector: s.selector || '.shopify-section'
          });
        });
      } catch (e) {}
    });
    return all;
  }

  // Paint section HTML from a /cart/* response into the DOM.
  function applySections(parsedState, sections) {
    if (!parsedState || !parsedState.sections) return;
    sections.forEach(function (section) {
      try {
        var targetEl = document.getElementById(section.id);
        if (!targetEl) return;
        var sectionHTML = parsedState.sections[section.section];
        if (!sectionHTML) return;
        var newDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
        var newInner = newDoc.querySelector(section.selector);
        if (!newInner) return;
        var paintInto = targetEl.querySelector(section.selector) || targetEl;
        paintInto.innerHTML = newInner.innerHTML;
      } catch (err) {
        console.warn('[tn-addons] applySection', section.id, err);
      }
    });
    // Sync is-empty class on cart drawer
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    }
  }

  // Open the cart drawer (after render).
  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-empty');
    try {
      if (typeof drawer.open === 'function') drawer.open();
      else drawer.classList.add('active', 'animate');
    } catch (e) {}
    // Scroll drawer to top so items are visible
    setTimeout(function () {
      var scrollable = drawer.querySelector('.drawer__inner-scrollable')
                    || drawer.querySelector('[class*="scrollable"]')
                    || drawer.querySelector('.drawer__inner')
                    || drawer;
      if (scrollable && scrollable.scrollTo) scrollable.scrollTo(0, 0);
      else if (scrollable) scrollable.scrollTop = 0;
    }, 50);
  }

  // ---- Inline button spinner (replaces button content on POST) ---
  function ensureSpinnerStyles() {
    if (document.getElementById('tn-spinner-styles')) return;
    var s = document.createElement('style');
    s.id = 'tn-spinner-styles';
    s.textContent =
      '.tn-btn-busy { pointer-events: none; opacity: 0.85; }' +
      '.tn-btn-loading {' +
      '  display: inline-flex; align-items: center; justify-content: center;' +
      '  gap: 8px; line-height: 1;' +
      '}' +
      '.tn-btn-loading svg {' +
      '  width: 16px; height: 16px;' +
      '  animation: tnspin 0.75s linear infinite;' +
      '}' +
      '@keyframes tnspin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
  }
  // Track original button content so we can restore it.
  var BTN_BUSY_KEY = '__tnBtnOriginal';
  function startBtnSpinner(button, label) {
    if (!button) return;
    ensureSpinnerStyles();
    if (button[BTN_BUSY_KEY] == null) {
      button[BTN_BUSY_KEY] = button.innerHTML;
    }
    button.classList.add('tn-btn-busy');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.innerHTML =
      '<span class="tn-btn-loading">' +
        '<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" ' +
            'stroke-width="5" stroke-linecap="round" ' +
            'stroke-dasharray="80 200" stroke-dashoffset="0" />' +
        '</svg>' +
        '<span>' + (label || 'Adding…') + '</span>' +
      '</span>';
  }
  function stopBtnSpinner(button) {
    if (!button) return;
    if (button[BTN_BUSY_KEY] != null) {
      button.innerHTML = button[BTN_BUSY_KEY];
      button[BTN_BUSY_KEY] = null;
    }
    button.classList.remove('tn-btn-busy');
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }

  // ---- Orphan F&P cleanup ----------------------------------------
  var cleanupInFlight = false;
  function cleanupOrphans() {
    if (cleanupInFlight) return Promise.resolve();
    cleanupInFlight = true;
    return getCart().then(function (cart) {
      var sareeHandles = {};
      var fpLines = [];
      cart.items.forEach(function (item) {
        if (String(item.variant_id) === String(FP_VARIANT_ID)) {
          fpLines.push({
            key: item.key,
            handle: item.properties && item.properties._saree_handle
          });
        } else if (item.handle) {
          sareeHandles[item.handle] = true;
        }
      });
      var orphans = fpLines.filter(function (fp) {
        return !fp.handle || !sareeHandles[fp.handle];
      });
      if (orphans.length === 0) return cart;
      console.log('[tn-addons] removing', orphans.length, 'orphan F&P');
      // Single /cart/update.js call: removes every orphan via the
      // `updates` map AND returns the rendered drawer sections in
      // the same response. One round-trip total.
      var updates = {};
      orphans.forEach(function (o) { updates[o.key] = 0; });
      var sections = collectSections();
      var body = { updates: updates };
      if (sections.length) {
        body.sections = sections.map(function (s) { return s.section; }).join(',');
        body.sections_url = window.location.pathname;
      }
      return postJSON('/cart/update.js', body).then(function (data) {
        if (sections.length && data && data.sections) {
          applySections(data, sections);
        }
        return cart;
      });
    }).catch(function (err) {
      console.warn('[tn-addons] cleanup', err);
    }).then(function () {
      cleanupInFlight = false;
    });
  }

  // Refresh cart UI without changing cart state.
  // Used after orphan cleanup so the drawer reflects new state.
  function refreshCartUI() {
    var sections = collectSections();
    if (!sections.length) return;
    var sectionNames = sections.map(function (s) { return s.section; }).join(',');
    fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        updates: {},
        sections: sectionNames,
        sections_url: window.location.pathname
      })
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) applySections(data, sections);
      }).catch(function () {});
  }

  // Debounced cleanup — just enough to coalesce simultaneous
  // events from the theme (fetch + cart:updated + cart-drawer:
  // updated etc. all fire within a few ms of each other).
  var cleanupTimer = null;
  function scheduleCleanup() {
    if (cleanupTimer) clearTimeout(cleanupTimer);
    cleanupTimer = setTimeout(function () {
      cleanupTimer = null;
      cleanupOrphans();
    }, 50);
  }

  // ---- Cart-mutation interception (fetch + XHR + events) ---------
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = '';
    try {
      url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    } catch (e) {}
    var promise = origFetch.apply(this, args);
    if (/\/cart\/(change|update|clear)(\.js)?/.test(url)) {
      promise.then(function () { scheduleCleanup(); }).catch(function () {});
    }
    return promise;
  };
  var origXHROpen = XMLHttpRequest.prototype.open;
  var origXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__tnUrl = url || '';
    return origXHROpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (/\/cart\/(change|update|clear)(\.js)?/.test(xhr.__tnUrl || '')) {
      xhr.addEventListener('load', function () { scheduleCleanup(); });
    }
    return origXHRSend.apply(this, arguments);
  };
  ['cart:updated', 'cart:refresh', 'cart:change',
   'cart-drawer:updated', 'cart:item-removed'
  ].forEach(function (evName) {
    document.addEventListener(evName, scheduleCleanup);
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleCleanup();
  });
  window.addEventListener('focus', scheduleCleanup);

  // ---- Module controller -----------------------------------------
  function init() {
    setTimeout(cleanupOrphans, 700);

    var module = document.querySelector('[data-tn-addons]');
    if (!module) return;

    var form = module.closest('form[action*="/cart/add"]')
            || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var qtyInput = form.querySelector('[name="quantity"]')
                || form.querySelector('input.quantity__input')
                || form.querySelector('quantity-input input[type="number"]')
                // Also try outside the form — qty input may use form="..." attr
                || document.querySelector('input.quantity__input[form="' + form.id + '"]')
                || document.querySelector('input[name="quantity"][form="' + form.id + '"]');

    // The Add-to-Bag submit button — we replace its content with a
    // spinner while the POST is in flight.
    function findSubmitButton() {
      return form.querySelector('button[type="submit"][name="add"]')
          || form.querySelector('button[type="submit"]')
          || form.querySelector('.product-form__submit')
          || form.querySelector('[name="add"]');
    }

    var parentPrice  = parseInt(module.getAttribute('data-tn-parent-price') || '0', 10);
    var parentHandle = module.getAttribute('data-tn-parent-handle') || '';
    var parentTitle  = module.getAttribute('data-tn-parent-title')  || '';

    function formatINR(paise) {
      var rs = paise / 100;
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency', currency: 'INR',
          minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(rs);
      } catch (e) { return '₹' + rs.toFixed(2); }
    }
    function readQty() {
      if (!qtyInput) return 1;
      var v = parseInt(qtyInput.value || '1', 10);
      return (!v || isNaN(v) || v < 1) ? 1 : v;
    }
    function readVariantId() {
      var inp = form.querySelector('[name="id"]');
      return inp ? parseInt(inp.value, 10) : null;
    }
    function getSelectedAddons() {
      var checks = module.querySelectorAll('[data-tn-addon-toggle]');
      var selected = [];
      Array.prototype.forEach.call(checks, function (cb) {
        if (!cb.checked) return;
        var card = cb.closest('[data-tn-addon]');
        if (!card) return;
        selected.push({
          variantId: parseInt(card.getAttribute('data-tn-addon-variant-id'), 10),
          price: parseInt(card.getAttribute('data-tn-addon-price'), 10) || 0,
          title: card.getAttribute('data-tn-addon-title') || ''
        });
      });
      return selected;
    }

    var totalEl    = module.querySelector('[data-tn-addons-total]');
    var totalValEl = module.querySelector('[data-tn-addons-total-value]');
    function updateSubtotal() {
      if (!totalEl || !totalValEl) return;
      var selected = getSelectedAddons();
      var qty = readQty();
      var total = parentPrice * qty;
      // Each selected add-on is added qty times to match the saree qty.
      selected.forEach(function (a) { total += a.price * qty; });
      totalValEl.textContent = formatINR(total);
      totalEl.hidden = (selected.length === 0);
    }
    module.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-tn-addon-toggle]')) return;
      updateSubtotal();
    });
    if (qtyInput) {
      qtyInput.addEventListener('change', updateSubtotal);
      qtyInput.addEventListener('input', updateSubtotal);
    }
    updateSubtotal();

    // ---- Single-POST Add to Bag intercept ----------------------
    var submitting = false;

    form.addEventListener('submit', function (e) {
      if (submitting) return;
      var selected = getSelectedAddons();  // may be empty — that's fine

      // ALWAYS intercept. The theme's native submit can't read
      // our form-associated qty input correctly via FormData,
      // so we drive the POST ourselves whether or not F&P
      // is ticked. Items array adapts to what's selected.
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      var submitBtn = findSubmitButton();
      startBtnSpinner(submitBtn, 'Adding…');
      clearError();
      submitting = true;

      var sareeVariantId = readVariantId();
      if (!sareeVariantId) {
        submitting = false;
        stopBtnSpinner(submitBtn);
        showError('Could not read variant. Please try again.');
        return;
      }
      var sareeQty = readQty();

      // SINGLE atomic items array.
      // F&P qty matches saree qty: buy 2 sarees → 2 F&P, etc.
      // Order: add-ons FIRST so the saree ends up on top in the
      // cart drawer (Shopify renders last-added first).
      var items = [];
      selected.forEach(function (a) {
        items.push({
          id: a.variantId,
          quantity: sareeQty,
          properties: {
            'For saree': parentTitle,
            '_saree_handle': parentHandle
          }
        });
      });
      items.push({ id: sareeVariantId, quantity: sareeQty });

      // Collect sections for response — using s.section (the API field).
      // collectSections() already trims to drawer + icon bubble.
      var sections = collectSections();
      var body = { items: items };
      if (sections.length) {
        body.sections = sections.map(function (s) { return s.section; }).join(',');
        body.sections_url = window.location.pathname;
      }

      postJSON('/cart/add.js', body)
        .then(function (data) {
          // Reset checkboxes
          var checks = module.querySelectorAll('[data-tn-addon-toggle]');
          Array.prototype.forEach.call(checks, function (cb) { cb.checked = false; });
          updateSubtotal();

          // Paint the new cart state into the drawer using the
          // CORRECT field for the API response lookup (s.section).
          if (sections.length && data.sections) {
            applySections(data, sections);
          }
          // Open the drawer the instant we have content
          openCartDrawer();
          // Restore the button immediately — drawer is now open
          stopBtnSpinner(submitBtn);
        })
        .catch(function (err) {
          console.error('[tn-addons]', err);
          stopBtnSpinner(submitBtn);
          showError(err && err.message
            ? err.message
            : 'Could not add to bag. Please try again.');
        })
        .then(function () { submitting = false; });
    }, true);

    function showError(message) {
      var wrap = form.querySelector('.product-form__error-message-wrapper');
      if (wrap) {
        wrap.toggleAttribute('hidden', false);
        var text = wrap.querySelector('.product-form__error-message');
        if (text) text.textContent = message;
        return;
      }
      var existing = module.querySelector('.tn-addons__err');
      if (existing) existing.remove();
      var err = document.createElement('div');
      err.className = 'tn-addons__err';
      err.style.cssText = 'margin-top:8px;color:#c0392b;font-size:12px;';
      err.textContent = message;
      module.appendChild(err);
    }
    function clearError() {
      var wrap = form.querySelector('.product-form__error-message-wrapper');
      if (wrap) wrap.toggleAttribute('hidden', true);
      var existing = module.querySelector('.tn-addons__err');
      if (existing) existing.remove();
    }
  }

  // ---- Debug helpers ---------------------------------------------
  window.tnDebug = function () {
    return getCart().then(function (cart) {
      console.log('%c=== CART ===', 'color:#8b6f3f;font-weight:bold');
      console.log('item_count:', cart.item_count);
      console.log('total_price:', '₹' + (cart.total_price / 100).toFixed(2));
      cart.items.forEach(function (item, i) {
        console.log('  [' + (i + 1) + ']',
          item.product_title,
          '\n    variant_id:', item.variant_id,
          '\n    quantity:', item.quantity,
          '\n    line_price:', '₹' + (item.line_price / 100).toFixed(2),
          '\n    properties:', item.properties);
      });
      return cart;
    });
  };
  window.tnCleanup = function () {
    return cleanupOrphans().then(function () {
      console.log('Cleanup complete.');
      return window.tnDebug();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();